import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  useMediaQuery,
  Card,
  CardMedia,
  Rating,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  TextareaAutosize,
  FormHelperText,
  IconButton,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  GridView,
  List as ListIcon,
  LocalShipping,
  Payment,
  CheckCircle,
  Receipt,
  LocationOn,
  CalendarToday,
  Download,
  Close,
} from "@mui/icons-material";
import Notes from "./header";
import FooterManual from "./footerManual";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import LandingPage from "./landingPage";

const api = axios.create({
  baseURL: "https://car.amar.co.jp/api",
  timeout: 10000, // Increased timeout
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Accept"] = "application/json";
    return config;
  },
  (error) => Promise.reject(error)
);

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
  overflow: "hidden",
  marginBottom: "16px",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    width: `calc(100% + ${theme.spacing(2)})`,
    "& .MuiTable-root": { minWidth: "650px" },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  [theme.breakpoints.down("sm")]: {
    "& > .MuiTableCell-root": { padding: theme.spacing(1) },
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    fontSize: "0.75rem",
  },
}));

const formatShippingOption = (shippingOption) => {
  if (!shippingOption) return "N/A";
  return shippingOption === "CNF" ? "C&F" : shippingOption;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiCardMedia-root": { height: "120px" },
    "& .MuiCardContent-root": { padding: "20px 8px" },
    "& .MuiButton-root": {
      fontSize: "0.7rem",
      padding: "4px 8px",
      minWidth: "unset",
    },
    "& .MuiTypography-h6": { fontSize: "0.9rem" },
    "& .MuiTypography-body2": { fontSize: "0.75rem" },
    "& .MuiChip-root": {
      height: "20px",
      "& .MuiChip-label": { fontSize: "0.65rem" },
    },
  },
}));

const SpinningLogo = styled("img")(({ theme }) => ({
  width: "60px",
  height: "60px",
  animation: "spin 1.5s linear infinite",
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const getDisplayTotalCost = (inquiry) => {
  const total = inquiry.charges?.totalCost ?? inquiry.totalCost;
  if (total && total > 0) {
    return total.toFixed(2);
  } else if (inquiry.carPriceValue && inquiry.carPriceValue > 0) {
    return inquiry.carPriceValue.toFixed(2);
  } else {
    return "0.00";
  }
};

const getVolume = (inquiry) => {
  return inquiry.volumeCubicMeters && inquiry.volumeCubicMeters > 0
    ? inquiry.volumeCubicMeters.toFixed(2) + " m³"
    : "N/A";
};

// ==================== COUNTRY CODE SELECT (same as MyProfile) ====================
// Get ISO code (IN, AU...) from flag emoji so user can search by "IN", "AU" etc.
const getIsoFromFlag = (flag = '') => {
  const chars = Array.from(flag);
  if (chars.length !== 2) return '';
  return chars
    .map((ch) => {
      const cp = ch.codePointAt(0);
      return cp >= 0x1F1E6 && cp <= 0x1F1FF ? String.fromCharCode(cp - 0x1F1E6 + 65) : '';
    })
    .join('');
};

// selectedId: countryId se exact country (US/Canada dono +1) - parent track karta hai
const CountryCodeSelect = ({ value, selectedId, onChange, options = [], disabled = false, error = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const filtered = options.filter(item =>
        item.country.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term) ||
        getIsoFromFlag(item.flag).toLowerCase() === term
      );
      // Priority: exact ISO match (IN -> India) > name startsWith > contains
      const isoMatch = [];
      const startsWith = [];
      const containsOnly = [];
      filtered.forEach(item => {
        if (getIsoFromFlag(item.flag).toLowerCase() === term) {
          isoMatch.push(item);
        } else if (item.country.toLowerCase().startsWith(term)) {
          startsWith.push(item);
        } else {
          containsOnly.push(item);
        }
      });
      setFilteredOptions([...isoMatch, ...startsWith, ...containsOnly]);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (item) => {
    onChange(item.code, item);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
  };

  const selectedItem =
    (selectedId && options.find(item => item.countryId === selectedId)) ||
    options.find(item => item.code === value);

  const isSelected = (item) => selectedItem && selectedItem.country === item.country;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? `${getIsoFromFlag(selectedItem.flag)} ${selectedItem.code}`.trim() : '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder="Search country code..."
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '16.5px 56px 16.5px 14px',
            border: error ? '2px solid #d32f2f' : '2px solid rgba(229, 62, 62, 0.3)',
            borderRadius: '12px',
            fontSize: '16px',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            transition: 'all 0.2s',
            height: '56px',
            ...(open && {
              borderColor: '#e53e3e',
              boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#eb2329';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown/search icon */}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#eb2329' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '300px',
          maxHeight: '300px',
          overflow: 'auto',
          zIndex: 1400,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((item, index) => (
              <div
                key={`${item.code}-${item.country}`}
                onClick={() => handleSelect(item)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  backgroundColor: isSelected(item) ? 'rgba(235, 35, 41, 0.12)' : 'white',
                  color: isSelected(item) ? '#eb2329' : '#374151',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected(item) ? 'rgba(235, 35, 41, 0.2)' : 'rgba(235, 35, 41, 0.08)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected(item) ? 'rgba(235, 35, 41, 0.12)' : 'white';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                {/* ISO code capital me (Admin/My Profile jaisa) */}
                <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '28px', color: '#111827' }}>{getIsoFromFlag(item.flag)}</span>
                <span>{item.code}</span>
                <span style={{ marginLeft: 'auto', color: isSelected(item) ? '#eb2329' : '#6b7280', fontSize: '13px', textAlign: 'right' }}>
                  {item.country}
                  {isSelected(item) && ' (Selected)'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Raise Ticket Dialog Component
const RaiseTicketDialog = ({ open, onClose, inquiry, onSuccess }) => {
  const [formData, setFormData] = useState({
    phone: '',
    countryCode: '+91', // Default to India
    inquiryType: '',
    subject: '',
    description: '',
    priority: '',
    attachments: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Field-level errors - saare required fields ek saath highlight honge
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);
  const [success, setSuccess] = useState('');
  // User's registered country code (from profile) - fallback India
  const [defaultCountryCode, setDefaultCountryCode] = useState('+91');
  // Profile wali exact country ka id (US vs Canada)
  const [defaultCountryId, setDefaultCountryId] = useState(null);

  // Country codes data
  // Complete country codes array with international dialing codes
  const countryCodes = [
    { code: '+213', flag: '🇩🇿', country: 'Algeria', countryId: 26 },
    { code: '+1-684', flag: '🇦🇸', country: 'American Samoa', countryId: 67 },
    { code: '+244', flag: '🇦🇴', country: 'Angola', countryId: 28 },
    { code: '+1-268', flag: '🇦🇬', country: 'Antigua', countryId: 82 },
    { code: '+61', flag: '🇦🇺', country: 'Australia', countryId: 50 },
    { code: '+1-242', flag: '🇧🇸', country: 'Bahamas', countryId: 83 },
    { code: '+973', flag: '🇧🇭', country: 'Bahrain', countryId: 59 },
    { code: '+880', flag: '🇧🇩', country: 'Bangladesh', countryId: 52 },
    { code: '+1-246', flag: '🇧🇧', country: 'Barbados', countryId: 84 },
    { code: '+32', flag: '🇧🇪', country: 'Belgium', countryId: 105 },
    { code: '+229', flag: '🇧🇯', country: 'Benin', countryId: 29 },
    { code: '+1-441', flag: '🇧🇲', country: 'Bermuda', countryId: 85 },
    { code: '+267', flag: '🇧🇼', country: 'Botswana', countryId: 17 },
    { code: '+673', flag: '🇧🇳', country: 'Brunei', countryId: 45 },
    { code: '+226', flag: '🇧🇫', country: 'Burkina Faso', countryId: 34 },
    { code: '+257', flag: '🇧🇮', country: 'Burundi', countryId: 9 },
    { code: '+237', flag: '🇨🇲', country: 'Cameroon', countryId: 30 },
    { code: '+1', flag: '🇨🇦', country: 'Canada', countryId: 104 },
    { code: '+1-345', flag: '🇰🇾', country: 'Cayman Island', countryId: 86 },
    { code: '+56', flag: '🇨🇱', country: 'Chile', countryId: 103 },
    { code: '+57', flag: '🇨🇴', country: 'Colombia', countryId: 87 },
    { code: '+225', flag: '🇨🇮', country: 'Cote D\'Ivoire', countryId: 31 },
    { code: '+357', flag: '🇨🇾', country: 'Cyprus', countryId: 115 },
    { code: '+243', flag: '🇨🇩', country: 'DR Congo', countryId: 12 },
    { code: '+1-767', flag: '🇩🇲', country: 'Dominica', countryId: 88 },
    { code: '+20', flag: '🇪🇬', country: 'Egypt', countryId: 24 },
    { code: '+291', flag: '🇪🇷', country: 'Eritrea', countryId: 3 },
    { code: '+251', flag: '🇪🇹', country: 'Ethiopia', countryId: 4 },
    { code: '+679', flag: '🇫🇯', country: 'Fiji', countryId: 68 },
    { code: '+358', flag: '🇫🇮', country: 'Finland', countryId: 106 },
    { code: '+33', flag: '🇫🇷', country: 'France', countryId: 107 },
    { code: '+241', flag: '🇬🇦', country: 'Gabon', countryId: 32 },
    { code: '+220', flag: '🇬🇲', country: 'Gambia', countryId: 33 },
    { code: '+49', flag: '🇩🇪', country: 'Germany', countryId: 108 },
    { code: '+233', flag: '🇬🇭', country: 'Ghana', countryId: 35 },
    { code: '+30', flag: '🇬🇷', country: 'Greece', countryId: 109 },
    { code: '+1-473', flag: '🇬🇩', country: 'Grenada', countryId: 89 },
    { code: '+590', flag: '🇬🇵', country: 'Guadeloupe', countryId: 90 },
    { code: '+224', flag: '🇬🇳', country: 'Guinea', countryId: 36 },
    { code: '+592', flag: '🇬🇾', country: 'Guyana', countryId: 91 },
    { code: '+509', flag: '🇭🇹', country: 'Haiti', countryId: 92 },
    { code: '+852', flag: '🇭🇰', country: 'Hongkong', countryId: 43 },
    { code: '+91', flag: '🇮🇳', country: 'India', countryId: 56 },
    { code: '+62', flag: '🇮🇩', country: 'Indonesia', countryId: 44 },
    { code: '+353', flag: '🇮🇪', country: 'Ireland', countryId: 110 },
    { code: '+1-876', flag: '🇯🇲', country: 'Jamaica', countryId: 93 },
    { code: '+254', flag: '🇰🇪', country: 'Kenya', countryId: 6 },
    { code: '+686', flag: '🇰🇮', country: 'Kiribati', countryId: 69 },
    { code: '+965', flag: '🇰🇼', country: 'Kuwait', countryId: 60 },
    { code: '+961', flag: '🇱🇧', country: 'Lebanon', countryId: 66 },
    { code: '+231', flag: '🇱🇷', country: 'Liberia', countryId: 37 },
    { code: '+218', flag: '🇱🇾', country: 'Libya', countryId: 25 },
    { code: '+261', flag: '🇲🇬', country: 'Madagascar', countryId: 21 },
    { code: '+265', flag: '🇲🇼', country: 'Malawi', countryId: 14 },
    { code: '+60', flag: '🇲🇾', country: 'Malaysia', countryId: 46 },
    { code: '+960', flag: '🇲🇻', country: 'Maldives', countryId: 55 },
    { code: '+356', flag: '🇲🇹', country: 'Malta', countryId: 117 },
    { code: '+692', flag: '🇲🇭', country: 'Marshall Islands', countryId: 70 },
    { code: '+596', flag: '🇲🇶', country: 'Martinique', countryId: 94 },
    { code: '+222', flag: '🇲🇷', country: 'Mauritania', countryId: 38 },
    { code: '+230', flag: '🇲🇺', country: 'Mauritius', countryId: 23 },
    { code: '+691', flag: '🇫🇲', country: 'Micronesia', countryId: 71 },
    { code: '+212', flag: '🇲🇦', country: 'Morocco', countryId: 27 },
    { code: '+258', flag: '🇲🇿', country: 'Mozambique', countryId: 15 },
    { code: '+95', flag: '🇲🇲', country: 'Myanmar', countryId: 53 },
    { code: '+264', flag: '🇳🇦', country: 'Namibia', countryId: 19 },
    { code: '+977', flag: '🇳🇵', country: 'Nepal', countryId: 54 },
    { code: '+31', flag: '🇳🇱', country: 'Netherland', countryId: 116 },
    { code: '+599', flag: '🇧🇶', country: 'Netherlands Antilles', countryId: 95 },
    { code: '+687', flag: '🇳🇨', country: 'New Caledonia', countryId: 72 },
    { code: '+64', flag: '🇳🇿', country: 'New Zealand', countryId: 51 },
    { code: '+234', flag: '🇳🇬', country: 'Nigeria', countryId: 39 },
    { code: '+47', flag: '🇳🇴', country: 'Norway', countryId: 111 },
    { code: '+968', flag: '🇴🇲', country: 'Oman', countryId: 61 },
    { code: '+92', flag: '🇵🇰', country: 'Pakistan', countryId: 57 },
    { code: '+680', flag: '🇵🇼', country: 'Palau', countryId: 73 },
    { code: '+507', flag: '🇵🇦', country: 'Panama', countryId: 96 },
    { code: '+675', flag: '🇵🇬', country: 'Papua New Guinea', countryId: 74 },
    { code: '+974', flag: '🇶🇦', country: 'Qatar', countryId: 62 },
    { code: '+262', flag: '🇷🇪', country: 'Reunion', countryId: 22 },
    { code: '+7', flag: '🇷🇺', country: 'Russia', countryId: 49 },
    { code: '+250', flag: '🇷🇼', country: 'Rwanda', countryId: 8 },
    { code: '+1-869', flag: '🇰🇳', country: 'ST. Kitts', countryId: 97 },
    { code: '+1-758', flag: '🇱🇨', country: 'ST. Lucia', countryId: 98 },
    { code: '+1-784', flag: '🇻🇨', country: 'ST. Vincent', countryId: 99 },
    { code: '+685', flag: '🇼🇸', country: 'Samoa', countryId: 75 },
    { code: '+966', flag: '🇸🇦', country: 'Saudi Arabia', countryId: 63 },
    { code: '+221', flag: '🇸🇳', country: 'Senegal', countryId: 40 },
    { code: '+232', flag: '🇸🇱', country: 'Sierra Leone', countryId: 41 },
    { code: '+65', flag: '🇸🇬', country: 'Singapore', countryId: 47 },
    { code: '+677', flag: '🇸🇧', country: 'Solomon Islands', countryId: 76 },
    { code: '+252', flag: '🇸🇴', country: 'Somalia', countryId: 5 },
    { code: '+27', flag: '🇿🇦', country: 'South Africa', countryId: 18 },
    { code: '+211', flag: '🇸🇸', country: 'South Sudan', countryId: 2 },
    { code: '+94', flag: '🇱🇰', country: 'Sri Lanka', countryId: 58 },
    { code: '+249', flag: '🇸🇩', country: 'Sudan', countryId: 1 },
    { code: '+597', flag: '🇸🇷', country: 'Suriname', countryId: 101 },
    { code: '+268', flag: '🇸🇿', country: 'Swaziland', countryId: 20 },
    { code: '+46', flag: '🇸🇪', country: 'Sweden', countryId: 112 },
    { code: '+689', flag: '🇵🇫', country: 'Tahiti', countryId: 77 },
    { code: '+255', flag: '🇹🇿', country: 'Tanzania', countryId: 11 },
    { code: '+66', flag: '🇹🇭', country: 'Thailand', countryId: 48 },
    { code: '+228', flag: '🇹🇬', country: 'Togo', countryId: 42 },
    { code: '+676', flag: '🇹🇴', country: 'Tonga', countryId: 78 },
    { code: '+1-868', flag: '🇹🇹', country: 'Trinidad & Tobago', countryId: 100 },
    { code: '+90', flag: '🇹🇷', country: 'Turkey', countryId: 113 },
    { code: '+688', flag: '🇹🇻', country: 'Tuvalu', countryId: 80 },
    { code: '+971', flag: '🇦🇪', country: 'U.A.E', countryId: 64 },
    { code: '+256', flag: '🇺🇬', country: 'Uganda', countryId: 7 },
    { code: '+44', flag: '🇬🇧', country: 'United Kingdom', countryId: 114 },
    { code: '+1', flag: '🇺🇸', country: 'United States', countryId: 79 },
    { code: '+678', flag: '🇻🇺', country: 'Vanuatu', countryId: 81 },
    { code: '+58', flag: '🇻🇪', country: 'Venezuela', countryId: 102 },
    { code: '+967', flag: '🇾🇪', country: 'Yemen', countryId: 65 },
    { code: '+260', flag: '🇿🇲', country: 'Zambia', countryId: 13 },
    { code: '+255', flag: '🇹🇿', country: 'Zanzibar', countryId: 10 },
    { code: '+263', flag: '🇿🇼', country: 'Zimbabwe', countryId: 16 },
    { code: '+81', flag: '🇯🇵', country: 'Japan', countryId: 121 }

  ];

  // Sort alphabetically by country name for better UX
  countryCodes.sort((a, b) => a.country.localeCompare(b.country));

  useEffect(() => {
    if (open && inquiry) {
      // Pre-fill form with inquiry data if available
      setFormData(prev => ({
        ...prev,
        subject: `Inquiry about ${inquiry.carBrandName || 'Car'} ${inquiry.carModelName || 'Model'}`,
        inquiryType: '',
        priority: '',
        description: ''
      }));
    }

    // Reset states when dialog opens
    if (open) {
      setError('');
      setSuccess('');
      setFieldErrors({});
    }
  }, [open, inquiry]);

  // Fetch user's registered country code from profile when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchUserCountryCode = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const response = await fetch('https://car.amar.co.jp/api/authorization/user/get/user/details', {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) return;

        const responseData = await response.json();
        if (cancelled) return;
        const userDetails = responseData?.userDetails || {};

        // Mobile number: strip country code if API sends '+61-9898...' (same as MyProfile)
        let mobile = String(userDetails.userMobileNumber || '');
        if (mobile.startsWith('+')) {
          const match = mobile.match(/\+\d+[-\s]*(\d+)/);
          mobile = match && match[1] ? match[1] : '';
        }
        mobile = mobile.replace(/\D/g, '');

        // API may return '61' or '+61' - normalize to '+61'
        const apiCode = userDetails.countryCode;
        const displayCode = apiCode
          ? (String(apiCode).startsWith('+') ? String(apiCode) : `+${apiCode}`)
          : '';

        // Only apply code if it exists in our dropdown list
        // Pehle profile ke countryId se exact country, na mile to dial code se
        const profileCountry =
          (userDetails.countryId && countryCodes.find(c => c.countryId === userDetails.countryId)) ||
          (displayCode && countryCodes.find(c => c.code === displayCode));
        if (profileCountry) {
          setDefaultCountryCode(profileCountry.code);
          setDefaultCountryId(profileCountry.countryId || null);
          setFormData(prev => ({ ...prev, countryCode: profileCountry.code, countryCodeId: profileCountry.countryId || null, phone: mobile }));
        } else if (mobile) {
          setFormData(prev => ({ ...prev, phone: mobile }));
        }
      } catch (err) {
        console.error('Failed to fetch user country code:', err);
      }
    };

    fetchUserCountryCode();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // User ne field bhara to uska error hatao
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCountryCodeChange = (code, item) => {
    setFormData(prev => {
      const newId = item?.countryId || null;
      // Same country = id same (US/Canada dono +1, isliye code se nahi)
      const sameCountry = !!item && (prev.countryCodeId ? newId === prev.countryCodeId : code === prev.countryCode);
      return {
        ...prev,
        countryCode: code,
        countryCodeId: newId,
        // Country badla to phone reset - user naya number daalega
        phone: sameCountry ? prev.phone : ''
      };
    });
    setFieldErrors(prev => ({ ...prev, countryCode: '' }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      attachments: e.target.files[0]
    }));
  };

  // Same as Order History - input bhi reset, taaki wahi file dobara select ho sake
  const removeAttachment = () => {
    setFormData(prev => ({
      ...prev,
      attachments: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    setError('');
    const errors = {};

    if (!formData.countryCode) errors.countryCode = 'Please select a country code';
    // Basic phone validation - allow different formats based on country
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9]{7,15}$/.test(cleanPhone)) {
      errors.phone = 'Please enter a valid phone number (7-15 digits)';
    }
    if (!formData.inquiryType) errors.inquiryType = 'Inquiry type is required';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.priority) errors.priority = 'Priority is required';
    if (!formData.description.trim()) errors.description = 'Description is required';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getCategoryFromInquiryType = (inquiryType) => {
    const categoryMap = {
      'GENERAL_INQUIRY': 'GENERAL_INQUIRY',
      'TECHNICAL': 'TECHNICAL_ISSUE',
      'BILLING': 'BILLING_QUESTION',
      'FEEDBACK': 'FEEDBACK_SUGGESTION'
    };
    return categoryMap[inquiryType] || 'GENERAL_INQUIRY';
  };

  // Format phone number according to API requirements: '+91-9876543210'
  const formatPhoneNumber = (countryCode, phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    return `${countryCode}-${cleanPhone}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Append all required fields as per the API
      if (inquiry && inquiry.calculationId) {
        formDataToSend.append('shippingCalculationId', inquiry.calculationId);
      }

      formDataToSend.append('ticketDescription', formData.description);
      formDataToSend.append('subject', formData.subject);

      // Format phone number correctly: '+91-9876543210'
      const formattedPhoneNumber = formatPhoneNumber(formData.countryCode, formData.phone);
      formDataToSend.append('alternativeMobileNumber', formattedPhoneNumber);

      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('category', getCategoryFromInquiryType(formData.inquiryType));

      // Append attachment if available
      if (formData.attachments) {
        formDataToSend.append('attachment', formData.attachments);
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');

      // Log the data being sent for debugging
      console.log('Sending phone number:', formattedPhoneNumber);

      const response = await axios.post(
        'https://car.amar.co.jp/api/dashboard/raise-ticket/for-inquiry',
        formDataToSend,
        {
          headers: {
            'Accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setSuccess(`Ticket created successfully! Ticket Number: ${response.data.ticketNumber}`);

        // Notify parent immediately to update button states
        if (onSuccess && inquiry) onSuccess(inquiry.calculationId);

        // Reset form on success
        setTimeout(() => {
          setFormData({
            phone: '',
            countryCode: defaultCountryCode,
          countryCodeId: defaultCountryId,
            inquiryType: '',
            subject: '',
            description: '',
            priority: '',
            attachments: null
          });
          onClose();
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to create ticket. Please try again.');
      }
    } catch (err) {
      console.error('Ticket creation error:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to create ticket. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      background: 'linear-gradient(135deg, #ffffff 0%, #fef7f7 100%)',
      borderRadius: '16px',
      border: '2px solid grey',
      boxShadow: '0 20px 40px rgba(229, 62, 62, 0.15)',
      maxHeight: '90vh',
      overflow: 'visible'
    },
    '& .MuiDialogTitle-root': {
      background: 'linear-gradient(135deg, #c92828ff 0%, #c92828ff 100%)',
      color: 'white',
      borderRadius: '14px 14px 0 0',
      margin: '-2px -2px 0 -2px',
      padding: '20px 24px',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60px',
        height: '4px',
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px'
      }
    },
    '& .MuiDialogContent-root': {
      padding: '30px 24px',
      background: 'rgba(255, 255, 255, 0.9)'
    },
    '& .MuiDialogActions-root': {
      padding: '20px 24px',
      background: 'rgba(254, 247, 247, 0.8)',
      borderTop: '1px solid rgba(229, 62, 62, 0.1)'
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(229, 62, 62, 0.3)',
        borderWidth: '2px'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(229, 62, 62, 0.6)'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#e53e3e',
        boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.1)'
      },
      '&.Mui-error fieldset': {
        borderColor: '#d32f2f'
      }
    },
    '& .MuiInputLabel-root': {
      color: '#666',
      '&.Mui-focused': {
        color: '#e53e3e'
      },
      '&.Mui-error': {
        color: '#d32f2f'
      }
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px'
    }
  };

  // Get formatted phone number for display
  const getFormattedDisplayNumber = () => {
    if (!formData.phone) return '_______';
    const cleanPhone = formData.phone.replace(/\D/g, '');
    return `${formData.countryCode}-${cleanPhone}`;
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      sx={dialogSx}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                🎫
              </Typography>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                color: 'white'
              }}
            >
              Raise Support Ticket
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            size="small"
            disabled={isSubmitting}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              minWidth: 'auto',
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            <Close />
          </Button>
        </Box>

        {/* Inquiry info banner (same style as Order History) */}
        {inquiry?.calculationId && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 'medium',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              🚗 Inquiry: #{inquiry.calculationId.toString().padStart(4, '0')}
              {inquiry.carRefNumber && (
                <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  (Car Ref: {inquiry.carRefNumber})
                </Typography>
              )}
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: '12px',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fecaca',
                '& .MuiAlert-icon': {
                  color: '#dc2626'
                }
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: '12px',
                backgroundColor: '#f0fdf4',
                color: '#16a34a',
                border: '1px solid #bbf7d0'
              }}
            >
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Phone Number with Country Code */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 'medium' }}>
                Phone Number*
              </Typography>
              <Box display="flex" gap={1}>
                <Box sx={{ width: '220px', flexShrink: 0 }}>
                  <CountryCodeSelect
                    value={formData.countryCode}
                    selectedId={formData.countryCodeId}
                    onChange={handleCountryCodeChange}
                    options={countryCodes}
                    disabled={isSubmitting}
                    error={!!fieldErrors.countryCode}
                  />
                </Box>

                <TextField
                  fullWidth
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                  disabled={isSubmitting}
                  error={!!fieldErrors.phone}
                  sx={fieldSx}
                  inputProps={{
                    maxLength: 15,
                    pattern: '[0-9]{7,15}'
                  }}
                />
              </Box>
              {/* Row ke neeche messages - taaki code aur phone box aligned rahein */}
              {fieldErrors.countryCode && (
                <FormHelperText error sx={{ ml: '4px' }}>{fieldErrors.countryCode}</FormHelperText>
              )}
              {fieldErrors.phone && (
                <FormHelperText error sx={{ ml: '4px' }}>{fieldErrors.phone}</FormHelperText>
              )}
              {/* <Typography variant="caption" sx={{ mt: 0.5, color: '#666', display: 'block' }}>
                Full number will be sent as: <strong>{getFormattedDisplayNumber()}</strong>
              </Typography> */}
              {/* <Typography variant="caption" sx={{ color: '#666', display: 'block', fontStyle: 'italic' }}>
                Format: Country Code + Hyphen + Phone Number (e.g., +91-9876543210)
              </Typography> */}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx} disabled={isSubmitting} error={!!fieldErrors.inquiryType}>
                <InputLabel>Inquiry Type*</InputLabel>
                <Select
                  name="inquiryType"
                  value={formData.inquiryType}
                  label="Inquiry Type*"
                  onChange={handleChange}
                  required
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="GENERAL_INQUIRY">General Inquiry</MenuItem>
                  <MenuItem value="TECHNICAL">Technical Issue</MenuItem>
                  <MenuItem value="BILLING">Billing Question</MenuItem>
                  <MenuItem value="FEEDBACK">Feedback/Suggestion</MenuItem>
                </Select>
                {fieldErrors.inquiryType && <FormHelperText>{fieldErrors.inquiryType}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                placeholder="Brief description of your issue"
                error={!!fieldErrors.subject}
                helperText={fieldErrors.subject}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx} disabled={isSubmitting} error={!!fieldErrors.priority}>
                <InputLabel>Priority*</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  label="Priority*"
                  onChange={handleChange}
                  required
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="LOW">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#10b981" />
                      Low
                    </Box>
                  </MenuItem>
                  <MenuItem value="MEDIUM">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#f59e0b" />
                      Medium
                    </Box>
                  </MenuItem>
                  <MenuItem value="HIGH">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#ef4444" />
                      High
                    </Box>
                  </MenuItem>
                  <MenuItem value="URGENT">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#dc2626" />
                      Urgent
                    </Box>
                  </MenuItem>
                </Select>
                {fieldErrors.priority && <FormHelperText>{fieldErrors.priority}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  height: '56px',
                  borderRadius: '12px',
                  borderColor: 'rgba(229, 62, 62, 0.3)',
                  borderWidth: '2px',
                  color: '#e53e3e',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    borderColor: 'rgba(229, 62, 62, 0.6)',
                    backgroundColor: 'rgba(254, 247, 247, 0.9)'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(229, 62, 62, 0.2)',
                    color: 'rgba(229, 62, 62, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                📎 Upload Attachment
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={isSubmitting}
                />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                placeholder="Please provide detailed information about your issue or inquiry..."
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
                sx={fieldSx}
              />
            </Grid>

            {formData.attachments && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(254, 247, 247, 0.8)',
                    border: '1px solid rgba(229, 62, 62, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      📄 Selected file:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e53e3e' }}>
                      {formData.attachments.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={removeAttachment}
                    disabled={isSubmitting}
                    aria-label="Remove file"
                    title="Remove file"
                    sx={{
                      color: '#e53e3e',
                      backgroundColor: 'rgba(229, 62, 62, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(229, 62, 62, 0.2)'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                📎 Max file size: 20MB (PDF, JPG, PNG only)
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            padding: '12px 24px',
            color: '#666',
            border: '2px solid rgba(17, 14, 14, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(254, 247, 247, 0.5)',
              borderColor: 'rgba(19, 17, 17, 0.4)'
            },
            '&:disabled': {
              color: 'rgba(102, 102, 102, 0.5)',
              borderColor: 'rgba(17, 14, 14, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="contained"
          sx={{
            borderRadius: '12px',
            padding: '12px 32px',
            background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
            boxShadow: '0 8px 20px rgba(229, 62, 62, 0.3)',
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: '16px',
            '&:hover': {
              background: 'linear-gradient(135deg, #c53030 0%, #b91c1c 100%)',
              boxShadow: '0 12px 24px rgba(229, 62, 62, 0.4)',
              transform: 'translateY(-2px)'
            },
            '&:disabled': {
              background: 'rgba(229, 62, 62, 0.6)',
              color: 'rgba(255, 255, 255, 0.7)',
              transform: 'none',
              boxShadow: 'none'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {isSubmitting ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              Submitting...
            </Box>
          ) : (
            '🚀 Submit Ticket'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const InquiryCardGrid = ({
  inquiry,
  onInquiryClick,
  onRaiseTicket,
  imageUrl,
  isMobile,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#4caf50";
      case "pending":
        return "#ff9800";
      case "rejected":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const dateOfInquiry = (inquiry) => {
    const dateString = inquiry.dateOfInquiry || inquiry.createdAt || inquiry.updatedAt;

    if (!dateString) {
      return "Recently";
    }

    // Handle DD-MM-YYYY format (from API)
    if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      return dateString.replace(/-/g, '/');
    }

    // Handle DD/MM/YYYY format
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }

    // Handle YYYY-MM-DD format (ISO format)
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = dateString.split('-');
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    try {
      // Try to parse the date string with Date object
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }

    // If all else fails, return the raw string or "Recently"
    return typeof dateString === 'string' ? dateString : "Recently";
  };

  return (
    <StyledCard
      onClick={() => onInquiryClick(inquiry.calculationId)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <CardMedia
          component="img"
          height="200"
          image={imageUrl || "/carousel/carslider.png"}
          alt={`Inquiry ${inquiry.calculationId || "N/A"}`}
          sx={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
            objectFit: "cover",
            minHeight: "200px"
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "bold",
          }}
        >
          #{inquiry.calculationId ? inquiry.calculationId.toString().padStart(4, "0") : "N/A"}
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: isMobile ? 1.5 : 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {inquiry.carBrandName || "Car"} {inquiry.carModelName || "Model"}
          </Typography>

          <Typography variant="body2" sx={{
            fontSize: isMobile ? "0.75rem" : "0.8125rem",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
          }}>
            <CalendarToday fontSize="small" />
            {dateOfInquiry(inquiry)}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: isMobile ? "0.75rem" : "0.8125rem",
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <LocationOn fontSize="small" />
            {inquiry.destination?.portName || inquiry.portName || "N/A"},{" "}
            {inquiry.destination?.countryName || inquiry.countryName || "N/A"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.65rem" }}
            >
              Shipping:
            </Typography>
            <Typography variant="body2">
              {formatShippingOption(inquiry.shippingOption)}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.65rem" }}
            >
              Status:
            </Typography>
            <Chip
              label={inquiry.status || "Completed"}
              size="small"
              sx={{
                height: "18px",
                fontSize: "0.6rem",
                backgroundColor: getStatusColor(inquiry.status || "completed"),
                color: "white",
                ml: 0.5,
              }}
            />
          </Box>

          <Box sx={{ gridColumn: "span 2" }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.65rem" }}
            >
              Total Cost:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: isMobile ? "1rem" : "1.125rem",
                fontWeight: 500,
                color: "error.main",
              }}
            >
              ${getDisplayTotalCost(inquiry)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            sx={{
              bgcolor: "transparent",
              color: "red",
              border: "1px solid red",
              borderRadius: "50px",
              fontSize: "0.75rem",
              padding: "18px 18px",
              minWidth: "150px",
              height: "38px",
              transition: "all 0.3s ease",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "red",
                transform: "translateX(-100%)",
                transition: "transform 0.3s ease",
                zIndex: -1,
              },
              "&:hover": {
                color: "white",
                boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                "&::before": {
                  transform: "translateX(0)",
                },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onInquiryClick(inquiry.calculationId);
            }}
          >
            View Details
          </Button>
          {/* <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "transparent",
              color: "red",
              border: "1px solid red",
              borderRadius: "50px",
              fontSize: "0.75rem",
              padding: "18px 18px",
              minWidth: "150px",
              height: "38px",
              transition: "all 0.3s ease",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "red",
                transform: "translateX(-100%)",
                transition: "transform 0.3s ease",
                zIndex: -1,
              },
              "&:hover": {
                color: "white",
                boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                "&::before": {
                  transform: "translateX(0)",
                },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onRaiseTicket(inquiry);
            }}
          >
            Raise Ticket
          </Button> */}
           <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "transparent",
              color: "red",
              border: "1px solid red",
              borderRadius: "50px",
              fontSize: "0.75rem",
              padding: "18px 18px",
              minWidth: "150px",
              height: "38px",
              transition: "all 0.3s ease",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "red",
                transform: "translateX(-100%)",
                transition: "transform 0.3s ease",
                zIndex: -1,
              },
              "&:hover": {
                color: "white",
                boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                "&::before": {
                  transform: "translateX(0)",
                },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onRaiseTicket(inquiry);
            }}
          >
            Raise Ticket
          </Button>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

const InquiryTable = ({
  inquiries,
  onInquiryClick,
  onRaiseTicket,
  imageUrl,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");
  const visibleColumns = isMobile ? 4 : isTablet ? 5 : 6;
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return "#4caf50";
      case "pending": return "#ff9800";
      case "rejected": return "#f44336";
      default: return "#757575";
    }
  };

  const dateOfInquiry = (inquiry) => {
    const dateString = inquiry.dateOfInquiry || inquiry.createdAt || inquiry.updatedAt;

    if (!dateString) {
      return "Recently";
    }

    // Directly handle the DD-MM-YYYY format from API
    if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      return dateString.replace(/-/g, '/');
    }

    // Handle DD/MM/YYYY format if present
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }

    // For any other date format, try to parse it
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }

    return "Recently";
  };

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell>Inquiry Details</StyledTableHeaderCell>
            {visibleColumns > 1 && (
              <StyledTableHeaderCell align="center">Amount</StyledTableHeaderCell>
            )}
            {visibleColumns > 2 && (
              <StyledTableHeaderCell align="center">Destination</StyledTableHeaderCell>
            )}
            {visibleColumns > 3 && (
              <StyledTableHeaderCell align="center">Status</StyledTableHeaderCell>
            )}
            {visibleColumns > 4 && (
              <StyledTableHeaderCell align="center">
                Inquiry Date
              </StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell align="center">Actions</StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {inquiries.map((inquiry) => (
            <StyledTableRow
              key={inquiry.calculationId || inquiry.id || Math.random()}
              onClick={() => onInquiryClick(inquiry.calculationId)}
            >
              <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: isMobile ? 60 : 80,
                      height: isMobile ? 45 : 60,
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        imageUrl[inquiry.carModelId] ||
                        "/carousel/carslider.png"
                      }
                      alt={`Inquiry ${inquiry.calculationId || "N/A"}`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 1,
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      #{inquiry.calculationId ? inquiry.calculationId.toString().padStart(4, "0") : "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      {inquiry.carBrandName || "Car"}{" "}
                      {inquiry.carModelName || "Model"}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              {visibleColumns > 1 && (
                <TableCell align="center">
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: isMobile ? "0.75rem" : "0.95rem",
                      color: "error.main",
                    }}
                  >
                    ${getDisplayTotalCost(inquiry)}
                  </Typography>
                </TableCell>
              )}

              {visibleColumns > 2 && (
                <TableCell align="center">
                  <Typography variant="body2">
                    {inquiry.destination?.portName || inquiry.portName || "N/A"}
                  </Typography>
                </TableCell>
              )}

              {visibleColumns > 3 && (
                <TableCell align="center">
                  <Chip
                    label={inquiry.status || "Completed"}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(inquiry.status || "completed"),
                      color: "white",
                      fontSize: "0.65rem",
                      height: "20px",
                    }}
                  />
                </TableCell>
              )}

              {visibleColumns > 4 && (
                <TableCell align="center">
                  <Typography sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
                    {dateOfInquiry(inquiry)}
                  </Typography>
                </TableCell>
              )}

              <TableCell align="center">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      bgcolor: "transparent",
                      color: "red",
                      border: "1px solid red",
                      borderRadius: "50px",
                      fontSize: "0.75rem",
                      padding: "18px 18px",
                      minWidth: "150px",
                      height: "38px",
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 1,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "red",
                        transform: "translateX(-100%)",
                        transition: "transform 0.3s ease",
                        zIndex: -1,
                      },
                      "&:hover": {
                        color: "white",
                        boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                        "&::before": {
                          transform: "translateX(0)",
                        },
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onInquiryClick(inquiry.calculationId);
                    }}
                  >
                    Details
                  </Button>
                  {/* <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "transparent",
                      color: "red",
                      border: "1px solid red",
                      borderRadius: "50px",
                      fontSize: "0.75rem",
                      padding: "18px 18px",
                      minWidth: "150px",
                      height: "38px",
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 1,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "red",
                        transform: "translateX(-100%)",
                        transition: "transform 0.3s ease",
                        zIndex: -1,
                      },
                      "&:hover": {
                        color: "white",
                        boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                        "&::before": {
                          transform: "translateX(0)",
                        },
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRaiseTicket(inquiry);
                    }}
                  >
                    Raise Ticket
                  </Button> */}
                   <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "transparent",
                      color: "red",
                      border: "1px solid red",
                      borderRadius: "50px",
                      fontSize: "0.75rem",
                      padding: "18px 18px",
                      minWidth: "150px",
                      height: "38px",
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 1,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "red",
                        transform: "translateX(-100%)",
                        transition: "transform 0.3s ease",
                        zIndex: -1,
                      },
                      "&:hover": {
                        color: "white",
                        boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                        "&::before": {
                          transform: "translateX(0)",
                        },
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRaiseTicket(inquiry);
                    }}
                  >
                    Raise Ticket
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!inquiry.isTicketRaised}
                    sx={{
                      bgcolor: !inquiry.isTicketRaised ? "#f5f5f5" : "transparent",
                      color: !inquiry.isTicketRaised ? "grey" : "red",
                      border: !inquiry.isTicketRaised ? "1px solid #ccc" : "1px solid red",
                      borderRadius: "50px",
                      fontSize: "0.75rem",
                      padding: "18px 18px",
                      minWidth: "150px",
                      height: "38px",
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 1,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: !inquiry.isTicketRaised ? "grey" : "red",
                        transform: "translateX(-100%)",
                        transition: "transform 0.3s ease",
                        zIndex: -1,
                      },
                      "&:hover": {
                        color: !inquiry.isTicketRaised ? "grey" : "white",
                        boxShadow: !inquiry.isTicketRaised ? "none" : "0 3px 6px rgba(255,0,0,0.3)",
                        "&::before": {
                          transform: !inquiry.isTicketRaised ? "translateX(-100%)" : "translateX(0)",
                        },
                      },
                      "&.Mui-disabled": {
                        color: "grey",
                        border: "1px solid #ccc",
                        bgcolor: "#f5f5f5",
                        cursor: "not-allowed",
                        pointerEvents: "auto",
                      },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (inquiry.isTicketRaised) {
                          navigate(`/myAccount/ticketHistory/${inquiry.calculationId}`)
                        }
                      }}
                  >
                    View Ticket
                  </Button>
                </Box>
              </TableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

const InquiryHistory = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);
  const [showInquiryDetails, setShowInquiryDetails] = useState(false);
  const [imageUrl, setImageUrl] = useState({});
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showInquiryDetailsDialog, setShowInquiryDetailsDialog] = useState(false);
  const [downloadingQuotation, setDownloadingQuotation] = useState(false);
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [showLanding, setShowLanding] = useState(false);
  const [showRaiseTicketDialog, setShowRaiseTicketDialog] = useState(false);
  const [selectedInquiryForTicket, setSelectedInquiryForTicket] = useState(null);

  const isMobile = useMediaQuery("(max-width:600px)");
  useEffect(() => {
    if (isMobile) {
      setViewMode("grid");   // Mobile → Grid
    } else {
      setViewMode("list");   // Desktop → List
    }
  }, [isMobile]);

  const navigate = useNavigate();

  const handleCloseDialog = () => {
    setSelectedInquiry(null);
  };

  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");
      if (!refreshTokenValue) {
        throw new Error("No refresh token available");
      }

      const response = await axios.post(
        "https://car.amar.co.jp/api/auth/refresh",
        { refreshToken: refreshTokenValue },
        { timeout: 5000 }
      );

      if (response.data?.accessToken) {
        localStorage.setItem("authToken", response.data.accessToken);
        if (response.data.refreshToken) {
          localStorage.setItem("refreshToken", response.data.refreshToken);
        }
        return response.data.accessToken;
      }
      throw new Error("Invalid token response");
    } catch (err) {
      console.error("Refresh failed:", err);
      localStorage.clear();
      navigate("/login");
      throw err;
    }
  }, [navigate]);

  const verifyAuth = useCallback(async () => {
    try {
      let token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token");
      }

      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        try {
          token = await refreshToken();
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          throw new Error("Session expired. Please login again.");
        }
      }

      const userId = decoded.userId || decoded.sub;
      if (!userId) {
        throw new Error("Invalid user in token");
      }

      return { token, userId };
    } catch (error) {
      console.error("Auth verification failed:", error);
      throw error;
    }
  }, [refreshToken]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    delete api.defaults.headers.Authorization;
    navigate("/login");
  }, [navigate]);

  const fetchCarImage = async (carModelId) => {
    try {
      if (!carModelId) return null;

      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`
      );

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error("Error fetching car image:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      if (inquiries && inquiries.length > 0) {
        const newImageUrls = {};

        for (const inquiry of inquiries) {
          if (inquiry.carModelId && !imageUrl[inquiry.carModelId]) {
            const fetchedImageUrl = await fetchCarImage(inquiry.carModelId);
            if (fetchedImageUrl) {
              newImageUrls[inquiry.carModelId] = fetchedImageUrl;
            }
          }
        }

        if (Object.keys(newImageUrls).length > 0) {
          setImageUrl(prev => ({ ...prev, ...newImageUrls }));
        }
      }
    };

    fetchImages();
  }, [inquiries]);

  const fetchCalculations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await api.get(
        "/authorization/shipping/get/user/calculation",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      if (response.data.success && response.data.data) {
        const mappedData = response.data.data.map((item) => ({
          calculationId: item.calculationId,
          carBrandName: item.carBrandName,
          carModelName: item.carModelName,
          carRefNumber: item.carRefNumber,
          carEngineCC: item.carEngineValue,
          carFuel: item.carFuelType,
          carMileage: item.carMileageValue,
          carDrive: item.carDriveType,
          carWeight: item.carWeight,
          carLength: item.carLength,
          carWidth: item.carWidth,
          carHeight: item.carHeight,
          carTransmission: item.carTransmission,
          carSteering: item.carSteering,
          carColor: item.carColor,
          shippingOption: item.shippingOption,
          portName: item.portName,
          countryName: item.countryName,
          carSize: item.carSize,
          userMessage: item.userMessage,
          totalCost: item.totalCost,
          baseShippingCost: item.baseShippingCost,
          volumeCubicMeters: item.volumeCubicMeters,
          carSeat: item.carSeatValue,
          carDoors: item.carDoors,
          airConditioning: item.airConditioning,
          carPriceValue: item.carPriceValue,
          carModelId: item.carModelId,
          dateOfInquiry: item.dateOfInquiry || item.createdAt || null,
          status: "Completed",
          // isTicketRaised: item.isTicketRaised || false,
          isTicketRaised: item.isTicketRaised === true,
          charges: {
            totalCost: item.totalCost,
            baseShippingCost: item.baseShippingCost,
            carPriceValue: item.carPriceValue,
          },
          destination: {
            portName: item.portName,
            countryName: item.countryName,
          },
        }));

        setInquiries(mappedData);
      } else {
        throw new Error(
          response.data.message || "Failed to fetch calculations"
        );
      }
    } catch (error) {
      console.error("Error fetching calculations:", error);
      setError(error.message || "Failed to load inquiry history");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalculations();
  }, [fetchCalculations]);

  const handleDownloadQuotation = async (calculationId) => {
    try {
      setDownloadingQuotation(true);
      const response = await api.get(`/authorization/user-quotation-invoice/download/${calculationId}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `quotation_${calculationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading quotation:", error);
      alert("Failed to download quotation. Please try again.");
    } finally {
      setDownloadingQuotation(false);
    }
  };

  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) setViewMode(newViewMode);
  };

  const handleInquiryClick = (calculationId) => {
    const inquiry = inquiries.find((inq) => inq.calculationId === calculationId);
    setSelectedCalculation(inquiry);
    setShowInquiryDetailsDialog(true);
  };

  const handleRaiseTicket = (inquiry) => {
    setSelectedInquiryForTicket(inquiry);
    setShowRaiseTicketDialog(true);
  };

  const handleCloseRaiseTicketDialog = () => {
    setShowRaiseTicketDialog(false);
    setSelectedInquiryForTicket(null);
  };

  const handleTicketSuccess = (calculationId) => {
    setInquiries(prev =>
      prev.map(inquiry =>
        inquiry.calculationId === calculationId
          ? { ...inquiry, isTicketRaised: true } // keep true so View Ticket stays enabled
          : inquiry
      )
    );
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleClick = () => {
    navigate("/myAccount");
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleCloseInquiryDialog = () => {
    setShowInquiryDetailsDialog(false);
    setSelectedCalculation(null);
  };

  const totalPages = Math.ceil(inquiries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInquiries = inquiries.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Notes />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Notes />

      {/* <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            // top: "10%",
            // left: "5%",
            top: { xs: "5%", sm: "5%", md: "5%" },
            left: { xs: "3%", sm: "5%", md: "5%" },
            margin: 0,
            color: "white",
            // fontSize: "15px",
            fontSize: { xs: "11px", sm: "13px", md: "15px" },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            // padding: "5px 10px",
            padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
            borderRadius: "5px",
          }}
        >
          <Typography
            component="span"
            sx={{
              opacity: 0.5,
              // marginRight: "5px",
               marginRight: { xs: "2px", sm: "5px", md: "5px" },
            }}
          >
            <Link
              to="http://3.110.209.177"
              style={{
                // fontSize: "13px",
                fontSize: "inherit",
                color: "white",
                textDecoration: "none",
              }}
            >
              Home/
            </Link>
          </Typography>
          <Link
            to="https://car.amar.co.jp/MyAccount"
            style={{ fontSize: "inherit", color: "white", textDecoration: "none" }}
          >
            My Account
          </Link>
          / InquiryHistory
        </Box>

        <img
          src="images/wishlist.png"
          alt="Inquiry History Background"
          style={{ width: "100%", height: "35vh", objectFit: "cover" }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textShadow: "2px 2px 8px rgba(0, 0, 0, 0.7)",
            fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "30px" },
            fontFamily: "Roboto",
            textAlign: "center",
            width: "100%",
            color: "#EBEBEB",
          }}
        >
          <b>Inquiry / History</b>
          <br />
          <button
            onClick={handleClick}
            style={{
              backgroundColor: "#EB2329",
              color: "white",
              border: "none",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "background-color 0.3s ease",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
          >
            My Account
          </button>
        </Box>
      </Box> */}
      <Box
        className="image-container-InquiryHistory"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "30vh", sm: "40vh", md: "50vh" },
          backgroundColor: "black",
        }}
      >
        <Box
          component="img"
          src="/images/wishlist.png"
          alt="Inquiry History Background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.3,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: { xs: "5%", sm: "5%", md: "5%" },
            left: { xs: "3%", sm: "5%", md: "5%" },
            margin: 0,
            color: "white",
            fontSize: { xs: "11px", sm: "13px", md: "15px" },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
            borderRadius: "5px",
          }}
        >
          <Typography
            component="span"
            sx={{
              opacity: 0.5,
              marginRight: { xs: "2px", sm: "5px", md: "5px" },
            }}
          >
            <Link
              to="http://3.110.209.177"
              style={{
                fontSize: "inherit",
                color: "white",
                textDecoration: "none",
              }}
            >
              Home/
            </Link>
          </Typography>

          <Link
            to="https://car.amar.co.jp/MyAccount"
            style={{
              fontSize: "inherit",
              color: "white",
              textDecoration: "none",
            }}
          >
            My Account
          </Link>

          / InquiryHistory
        </Box>

        <Box
          className="support-paragraph-InquiryHistory"
          sx={{
            position: "absolute",
            margin: "auto",
            color: "white",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "30px" },
            fontFamily: "Roboto",
            fontWeight: "700",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "10px", sm: "15px", md: "20px" },
            textAlign: "center",
            width: "90%",
          }}
        >
          Inquiry / History

          <button
            onClick={handleClick}
            style={{
              backgroundColor: "#EB2329",
              color: "white",
              border: "none",
              padding:
                window.innerWidth < 600
                  ? "8px 16px"
                  : window.innerWidth < 960
                    ? "9px 18px"
                    : "10px 20px",
              fontSize:
                window.innerWidth < 600
                  ? "14px"
                  : window.innerWidth < 960
                    ? "15px"
                    : "16px",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "background-color 0.3s ease",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
          >
            My Account
          </button>
        </Box>
      </Box>


      <Container
        maxWidth="xl"
        sx={{
          width: "90%",
          backgroundColor: "#FFFFFF",
          border: "2px solid #EBEBEB",
          borderRadius: "10px",
          marginTop: "-75px",
          marginBottom: "100px",
          position: "relative",
          zIndex: 1,
          minHeight: "400px",
          p: { xs: 2, md: 4 },
        }}
      >
        {error && inquiries.length === 0 ? (
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
              textAlign: "center"
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
              We couldn't find what you're looking for
            </Typography>
            <Typography sx={{ mb: 3 }}>
              The page you requested is not available or may have been moved.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{
                backgroundColor: "red",
                color: "white",
                "&:hover": {
                  backgroundColor: "#d32f2f"
                }
              }}
            >
              Go to Home Page
            </Button>
          </Box>
        ) : inquiries.length === 0 ? (
          <Box sx={{ flex: 1, p: 3, pt: 10, textAlign: "center" }}>
            <Typography variant="h6" mb={2} color="error">
              Welcome to your Inquiry Page!
            </Typography>
            <Typography mb={1}>
              You currently do not have any ongoing inquiries with us. We are
              here to assist you with your questions and inquiries!
            </Typography>
            <Typography mb={1}>
              Once you make an inquiry about a car, your inquiries will be
              displayed here. We will keep you updated with all necessary
              information regarding your inquiries.
            </Typography>
            <Typography mb={3}>
              Feel free to check the available cars!
            </Typography>
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowLanding(true)}
                sx={{ mt: 2, backgroundColor: "red" }}
              >
                Browse Cars
              </Button>
              {showLanding && <LandingPage />}
            </div>
          </Box>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 2 : 3,
                mb: 4,
                borderRadius: "10px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: isMobile ? 2 : 0,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      fontSize: isMobile ? "1.25rem" : "1.5rem",
                      color: "error.main",
                    }}
                  >
                    Inquiry History
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {inquiries.length}{" "}
                    {inquiries.length === 1 ? "inquiry" : "inquiries"} found
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewMode}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiToggleButton-root": {
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      color: "black",
                      "&.Mui-selected": {
                        backgroundColor: "red",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#cc0000",
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="list">
                    <ListIcon fontSize={isMobile ? "small" : "medium"} />
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <GridView fontSize={isMobile ? "small" : "medium"} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Paper>

            {/* {viewMode === "list" ? (
              <InquiryTable
                inquiries={paginatedInquiries}
                onInquiryClick={handleInquiryClick}
                onRaiseTicket={handleRaiseTicket}
                imageUrl={imageUrl}
              />
            ) : (
              <Grid container spacing={isMobile ? 2 : 3}>
                {paginatedInquiries.map((inquiry) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={inquiry.calculationId}>
                    <InquiryCardGrid
                      inquiry={inquiry}
                      onInquiryClick={handleInquiryClick}
                      onRaiseTicket={handleRaiseTicket}
                      imageUrl={
                        imageUrl[inquiry.carModelId] || "/carousel/carslider.png"
                      }
                      isMobile={isMobile}
                    />
                  </Grid>
                ))}
              </Grid>
            )} */}
            {viewMode === "grid" ? (
              <Grid container spacing={2}>
                {inquiries.map((inquiry) => (
                  <Grid item xs={12} sm={6} md={4} key={inquiry.calculationId}>
                    <InquiryCardGrid
                      inquiry={inquiry}
                      onInquiryClick={handleInquiryClick}
                      onRaiseTicket={handleRaiseTicket}
                      imageUrl={imageUrl[inquiry.carModelId]}
                      isMobile={isMobile}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <InquiryTable
                inquiries={inquiries}
                onInquiryClick={handleInquiryClick}
                onRaiseTicket={handleRaiseTicket}
                imageUrl={imageUrl}
              />
            )}

            {inquiries.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 4,
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "black",
                      fontWeight: 500,
                      letterSpacing: "0.5px",
                    }}
                  >
                    Results per page:
                  </Typography>
                </Box>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 500,
                      borderColor: "black",
                      color: "black",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.1)",
                        borderColor: "red",
                      },
                    },
                    "& .Mui-selected": {
                      backgroundColor: "red !important",
                      color: "white",
                      borderColor: "red",
                      "&:hover": {
                        backgroundColor: "#b30000 !important",
                        borderColor: "#800000",
                      },
                    },
                  }}
                />
                <Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: 80,
                    color: "black",
                    fontWeight: 500,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      border: "1px solid black !important",
                    },
                    "&:hover fieldset": {
                      borderColor: "red !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "red !important",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "red",
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Box>
            )}

            {/* Raise Ticket Dialog */}
            <RaiseTicketDialog
              open={showRaiseTicketDialog}
              onClose={handleCloseRaiseTicketDialog}
              inquiry={selectedInquiryForTicket}
              onSuccess={handleTicketSuccess}
            />

            {/* Inquiry Details Dialog */}
            <Dialog
              open={showInquiryDetailsDialog}
              onClose={handleCloseInquiryDialog}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    Inquiry Details #
                    {selectedCalculation?.calculationId
                      ?.toString()
                      .padStart(4, "0") || "N/A"}
                  </Typography>
                  <Box>
                    {selectedCalculation && selectedCalculation.shippingOption && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          handleDownloadQuotation(
                            selectedCalculation.calculationId
                          )
                        }
                        disabled={downloadingQuotation}
                        sx={{ mr: 2, backgroundColor: "red" }}
                      >
                        {downloadingQuotation
                          ? "Downloading..."
                          : "Download Quotation"}
                      </Button>
                    )}
                    <Button
                      onClick={handleCloseInquiryDialog}
                      sx={{ minWidth: "auto", p: 1, color: "Red" }}
                    >
                      ×
                    </Button>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent>
                {selectedCalculation ? (
                  <>
                    <Card
                      sx={{
                        mb: 3,
                        overflow: "hidden",
                        borderRadius: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                        }}
                      >
                        <Link
                          to={`/product/${selectedCalculation.carModelId}`}
                        >
                          <CardMedia
                            component="img"
                            image={
                              imageUrl[selectedCalculation.carModelId] ||
                              "/carousel/carslider.png"
                            }
                            alt="Vehicle"
                            sx={{
                              width: { xs: "100%", md: "300px" },
                              height: { xs: 200, md: 250, marginTop: 20 },
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                        </Link>
                        <CardContent sx={{ flex: 1 }}>
                          <Link
                            to={`/product/${selectedCalculation.carModelId}`}
                            style={{ textDecoration: "none", color: "black", }}
                          >
                            <Typography
                              variant="h5"
                              sx={{ mb: 1, color: "primary.m", fontWeight: "bold" }}
                            >
                              {selectedCalculation.carBrandName?.replace(/_/g, " ") || "N/A"}{" "}
                              {selectedCalculation.carModelName || "N/A"}
                            </Typography>
                          </Link>

                          <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              width: "100%",
                              overflowX: "auto"
                            }}
                          >
                            <Table size="small" sx={{ minWidth: 650 }} aria-label="car details table">
                              <TableBody>
                                <TableRow>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold", width: '20%' }}>
                                    Engine CC
                                  </TableCell>
                                  <TableCell sx={{ width: '30%' }}>
                                    {selectedCalculation.carEngineCC || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold", width: '20%' }}>
                                    Fuel Type
                                  </TableCell>
                                  <TableCell sx={{ width: '30%' }}>
                                    {selectedCalculation.carFuel || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Mileage
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carMileage || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Drive Type
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carDrive || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Transmission
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carTransmission || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Steering
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carSteering || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Color
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carColor || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Seats
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carSeat || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Air Conditioning
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.airConditioning || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "grey", fontWeight: "bold" }}>
                                    Doors
                                  </TableCell>
                                  <TableCell>
                                    {selectedCalculation.carDoors || "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                            }}
                          >
                            <Box textAlign="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Volume
                              </Typography>
                              <Typography variant="h6" color="black">
                                {getVolume(selectedCalculation)}
                              </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box textAlign="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Shipping
                              </Typography>
                              <Typography variant="h6" color="black">
                                {formatShippingOption(selectedCalculation.shippingOption)}
                              </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box textAlign="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Total Cost
                              </Typography>
                              <Typography variant="h6" color="error">
                                ${getDisplayTotalCost(selectedCalculation)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Box>
                    </Card>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ mb: 2 }}
                          >
                            Shipping Charges
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Car Base Price</TableCell>
                                  <TableCell align="right">
                                    ${selectedCalculation.carPriceValue?.toFixed(2) || "0.00"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell> Shipping Cost</TableCell>
                                  <TableCell align="right">
                                    ${selectedCalculation.baseShippingCost?.toFixed(2) || "0.00"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: "bold" }}>Total Cost</TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "error.main",
                                      fontSize: "1rem"
                                    }}
                                  >
                                    ${getDisplayTotalCost(selectedCalculation)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ mb: 2 }}
                          >
                            Destination Details
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Port</TableCell>
                                  <TableCell>
                                    {selectedCalculation.portName || "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Country</TableCell>
                                  <TableCell>
                                    {selectedCalculation.countryName || "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {selectedCalculation.userMessage && (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                Additional Notes:
                              </Typography>
                              <Typography variant="body2">
                                {selectedCalculation.userMessage}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,

                    }}
                  >
                    <Typography>Loading inquiry details...</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseInquiryDialog}
                  sx={{ color: "Red" }}>

                  Close</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Container>

      <FooterManual />

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default InquiryHistory;